import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { formatDate } from "@angular/common";
import {
  faBan,
  faPencil,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FormField } from "../../../../interfaces/form-field.model";
import { ProspectService } from "src/app/services/customer/prospect.service";
import { NotifierService } from "angular-notifier";
import { of, switchMap, tap } from "rxjs";
import { isEmptyGuid } from "../../../../services/util.service";
import { CommunicationPreferences } from "src/app/models/administration/prospect/prospect.model";

@Component({
  selector: "app-prospect-detail",
  templateUrl: "./prospect-detail.component.html",
  styleUrls: ["./prospect-detail.component.css"],
})
export class ProspectDetailComponent implements OnInit {
  // ─── Icons ───────────────────────────────────────────────
  faPencil = faPencil;
  faBan = faBan;
  faPlus = faPlus;

  // ─── State ───────────────────────────────────────────────
  mode = "Add";
  userId = "00000000-0000-0000-0000-000000000000";
  lockSave = true;
  activeUsers: any[] = [];
  preferences = new CommunicationPreferences();
  preferencesAll = false;

  // ─── Form ────────────────────────────────────────────────
  prospect: FormField[] = [
    {
      type: "text",
      id: "IcNo",
      label: "NRIC No/RCB No/Passport No",
      required: true,
    },
    {
      type: "select",
      id: "Title",
      label: "Title",
      required: true,
      options: [
        { display: "MR", value: "MR" },
        { display: "MRS", value: "MRS" },
        { display: "MISS", value: "MISS" },
        { display: "M/S", value: "M/S" },
        { display: "MDM", value: "MDM" },
        { display: "DR", value: "DR" },
        { display: "MS", value: "MS" },
      ],
    },
    {
      type: "text",
      id: "Name",
      label: "Name as in NRIC/Passport",
      required: true,
    },
    {
      type: "text",
      id: "LicenseNo",
      label: "Driving licence number",
    },
    {
      type: "text",
      id: "Hp",
      label: "Mobile number",
      required: true,
    },
    { type: "text", id: "AddressBlock", label: "Block/House" },
    { type: "text", id: "AddressRoad", label: "Road" },
    { type: "text", id: "AddressUnit", label: "Unit" },
    { type: "text", id: "PostalCode", label: "Postal code" },
    { type: "email", id: "Email", label: "Email", required: true },
    {
      type: "date",
      id: "Dob",
      label: "Date Of birth (dd/mm/yyyy)",
      required: true,
    },
    {
      type: "select",
      id: "ModeContact",
      label: "Mode of contact",
      options: [
        { display: "", value: "" },
        { display: "REFERRAL", value: "REF" },
        { display: "REPEAT", value: "RPT" },
        { display: "WALK-IN", value: "WI" },
        { display: "CALL-IN", value: "OC" },
      ],
    },
    {
      type: "select",
      id: "ModelInterested",
      label: "Model interested",
      required: true,
    },
    { type: "text", id: "CurrentCarModel", label: "Current car" },
    {
      type: "text",
      id: "CurrentRegNo",
      label: "Registration number",
    },
    {
      type: "date",
      id: "CurrentRegDate",
      label: "Registration date (dd/mm/yyyy)",
    },
    {
      type: "select",
      id: "SalesConsultant",
      label: "Sales consultant",
    },
    { type: "text", id: "Remarks", label: "Remarks" },
    {
      type: "select",
      id: "Source",
      label: "Where did you find out about our product/offer?",
      options: [
        { display: "", value: "" },
        { display: "Newspaper", value: "Newspaper" },
        { display: "Magazine", value: "Magazine" },
        { display: "Radio", value: "Radio" },
        { display: "Internet", value: "Internet" },
        { display: "Others", value: "Others" },
      ],
    },
    { type: "text", id: "AltSource", label: "Please specify" },
    {
      type: "formCheckbox",
      id: "IsBought",
      label: "Already bought the car",
    },
  ];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private prospectService: ProspectService,
    private notifier: NotifierService,
  ) {}

  // ─── Lifecycle ───────────────────────────────────────────

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        tap((data) => {
          const Id = data["id"];
          this.fetchSCAndModels().subscribe({
            next: () => this.setUpFormFields(Id),
            error: (error) => console.error(error),
          });
        }),
      )
      .subscribe();
  }

  // ─── Data Loading ────────────────────────────────────────

  private fetchSCAndModels() {
    return of(null).pipe(
      switchMap(() =>
        this.prospectService.getModelActiveInSalesDrpList().pipe(
          tap((response) => this.processActiveModels(response.body)),
          switchMap(() => this.prospectService.getAllActiveUsers()),
          tap((response) => {
            this.activeUsers = response.body.map((user) => ({
              display: user.display_name,
              value: user.user_name,
              id: user.user_id,
            }));
            this.getField("SalesConsultant").options =
              this.activeUsers;
          }),
        ),
      ),
    );
  }

  private setUpFormFields(Id: string): void {
    this.prospectService.GetCustomerDetail(Id).subscribe({
      next: (response) => {
        const body = response.body;
        this.setUserFormFields(body);
        this.checkAllowSave();
        this.userId = body.CustomerId;
        if (!isEmptyGuid(Id)) this.mode = "Detail";
        this.preferences.DoNotReceive = body.DoNotReceive;
        this.preferences.IsAgreeDirectMail = body.IsAgreeDirectMail;
        this.preferences.IsAgreeEMail = body.IsAgreeEMail;
        this.preferences.IsAgreePhone = body.IsAgreePhone;
        this.preferences.IsAgreeSMS = body.IsAgreeSMS;
      },
      error: (error) => console.error(error),
    });
  }

  private processActiveModels(responseBody: any[]): void {
    this.getField("ModelInterested").options = responseBody.map(
      (model) => ({
        display: model.name,
        value: model.name,
      }),
    );
  }

  // ─── Form Population ─────────────────────────────────────

  private setUserFormFields(responseBody: any): void {
    const dateFields = ["Dob", "CurrentRegDate"];
    const knownSources = [
      "Newspaper",
      "Magazine",
      "Radio",
      "Internet",
    ];

    this.prospect = this.prospect.map((field: any) => {
      if (dateFields.includes(field.id)) {
        const val = responseBody[field.id];
        return { ...field, value: val ? this.parseDate(val) : "" };
      }
      if (field.id === "SalesConsultant") {
        return {
          ...field,
          value: this.activeUsers.find(
            (item) => item.id == responseBody.UserOwner,
          )?.value,
        };
      }
      if (field.id === "Source") {
        return {
          ...field,
          value: knownSources.includes(responseBody.Source)
            ? responseBody.Source
            : "Others",
        };
      }
      if (field.id === "AltSource") {
        return {
          ...field,
          lock: knownSources.includes(responseBody.Source),
          value: !knownSources.includes(responseBody.Source)
            ? responseBody.Source
            : "",
        };
      }
      return {
        ...field,
        value: responseBody[field.id] ?? field.value,
      };
    });
  }

  // ─── Form Events ─────────────────────────────────────────

  updateFields(event: any): void {
    this.prospect = event.fields;
    this.checkAllowSave();
    if (this.prospect[18].value === "Others") {
      this.prospect[18].id = "AltSource";
      this.prospect[19].id = "Source";
      this.prospect[19].lock = false;
    } else {
      this.prospect[18].id = "Source";
      this.prospect[19].id = "AltSource";
      this.prospect[19].value = "";
      this.prospect[19].lock = true;
    }
  }

  // ─── Communication Preferences ───────────────────────────

  toggleAll(event: any): void {
    const isChecked = event.target.checked;
    this.preferences.IsAgreeDirectMail = isChecked;
    this.preferences.IsAgreeEMail = isChecked;
    this.preferences.IsAgreeSMS = isChecked;
    this.preferences.IsAgreePhone = isChecked;
    this.preferences.DoNotReceive = false;
  }

  toggleNoCommunication(event: any): void {
    if (event.target.checked) {
      this.preferences.IsAgreeDirectMail = false;
      this.preferences.IsAgreeEMail = false;
      this.preferences.IsAgreeSMS = false;
      this.preferences.IsAgreePhone = false;
      this.preferencesAll = false;
    }
  }

  checkSelected(): void {
    this.preferences.DoNotReceive = false;
    this.preferencesAll =
      this.preferences.IsAgreeDirectMail &&
      this.preferences.IsAgreeEMail &&
      this.preferences.IsAgreeSMS &&
      this.preferences.IsAgreePhone;
  }

  // ─── Save / Update ───────────────────────────────────────

  addProspect(): void {
    this.prospectService
      .SaveProspectCustomer(this.buildPayload())
      .subscribe({
        next: () => {
          this.notifier.notify("success", "Customer added");
          this.navigateToList();
        },
        error: (error) => console.error(error),
      });
  }

  updateProspect(): void {
    this.prospectService
      .UpdateProspectCustomer(this.userId, this.buildPayload())
      .subscribe({
        next: () => {
          this.notifier.notify("success", "Customer updated");
          this.navigateToList();
        },
        error: (error) => console.error(error),
      });
  }

  private buildPayload(): any {
    const isNew = this.mode === "Add";
    const v = (id: string) => this.getFieldValue(id) ?? "";
    const formatDt = (id: string) => {
      const val = this.getFieldValue(id);
      return val ? formatDate(val, "dd/MM/yyyy", "en-US") : "";
    };
    const userOwner =
      this.activeUsers.find(
        (item) => item.value == this.getFieldValue("SalesConsultant"),
      )?.id ?? "";

    return {
      AddressBlock: v("AddressBlock"),
      AddressRoad: v("AddressRoad"),
      AddressUnit: v("AddressUnit"),
      Category: isNew ? "" : "PROSPECT",
      CreatedBy: "",
      CreatedDate: "",
      CurrentCarModel: v("CurrentCarModel"),
      CurrentRegDate: formatDt("CurrentRegDate"),
      CurrentRegNo: v("CurrentRegNo"),
      CustomerId: isNew ? 0 : this.userId,
      DoNotReceive: this.preferences.DoNotReceive ?? false,
      Dob: formatDate(
        this.getFieldValue("Dob"),
        "dd/MM/yyyy",
        "en-US",
      ),
      Email: v("Email"),
      FamilyName: "",
      GivenName: "",
      Hp: v("Hp"),
      IcNo: v("IcNo"),
      IsAgreeDirectMail: this.preferences.IsAgreeDirectMail ?? true,
      IsAgreeEMail: this.preferences.IsAgreeEMail ?? true,
      IsAgreePhone: this.preferences.IsAgreePhone ?? true,
      IsAgreeSMS: this.preferences.IsAgreeSMS ?? true,
      IsBought: this.getFieldValue("IsBought") ?? false,
      IsDeleted: false,
      LastModifiedBy: "",
      LastModifiedDate: "",
      LicenseNo: v("LicenseNo"),
      ModeContact: v("ModeContact"),
      ModelInterested: v("ModelInterested"),
      Name: v("Name"),
      PostalCode: v("PostalCode"),
      Remarks: v("Remarks"),
      ResidenceNo: isNew ? "" : "0",
      Source: v("Source"),
      Title: v("Title"),
      UserOwner: userOwner,
      // Add-only fields
      ...(isNew && {
        AddedFromTestDrive: "",
        Bought: "",
        Gender: "",
        Occupation: "",
        OfficeNo: "",
        Status: "",
        IsIsAgreeDirectMail: false,
        IsIsAgreePhone: false,
        IsIsAgreeSMS: false,
        modelinterestedothers: "",
      }),
      // Update-only fields
      ...(!isNew && {
        IsAddedFromTestDrive: true,
        Occupation: null,
        OfficeNo: null,
        Status: null,
      }),
    };
  }

  // ─── Helpers ─────────────────────────────────────────────

  private navigateToList(): void {
    this.router.navigate(["../../prospect"], {
      relativeTo: this.activatedRoute,
    });
  }

  private parseDate(dateStr: string): string {
    const [day, month, year] = dateStr.split("/").map(Number);
    const date = new Date(year, month - 1, day);
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  }

  private checkAllowSave(): void {
    this.lockSave = this.prospect
      .filter((item) => item.required)
      .every((item) => item.value);
  }

  private getFieldValue(id: string, index?: number): any {
    const field = this.prospect.find((f) => f.id === id);
    return index !== undefined ? field?.value[index] : field?.value;
  }

  getField(id: string): FormField {
    return (
      this.prospect.find((f) => f.id === id) || {
        id: "",
        type: "text",
        label: "",
        value: undefined,
      }
    );
  }
}
